import { InMemoryProvider, OpenFeature } from "@openfeature/react-sdk";
import { FlagsmithClientProvider } from "@openfeature/flagsmith-client-provider";
import { FirstSuccessfulStrategy, WebMultiProvider } from "@openfeature/multi-provider-web";

import flags from "./flags";

const salesforceEnvValue = import.meta.env.REACT_APP_FEATURE_FLAG_SALESFORCE_ENABLED;
const salesforceDefaultVariant =
  salesforceEnvValue === "true" || salesforceEnvValue === true ? "true" : "false";
const subscribeEnvValue = import.meta.env.REACT_APP_FEATURE_FLAG_SUBSCRIBE_ENABLED;
const subscribeDefaultVariant =
  subscribeEnvValue === "true" || subscribeEnvValue === true ? "true" : "false";
function createFlagConfig() {
  const config = {
    [flags.SALESFORCE]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: salesforceDefaultVariant,
    },
    [flags.SUBSCRIBE_ENABLED]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: subscribeDefaultVariant,
    },
    [flags.APP_MODERNISATION]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_APP_MODERNISATION || "false",
      // contextEvaluator: (context) => {
      //   // if (import.meta.env.REACT_APP_FEATURE_FLAG_APP_MODERNISATION === "true") {
      //   // //   return returnFalseIfRunningViaAutomation();
      //   // }
      //   return import.meta.env.REACT_APP_FEATURE_FLAG_APP_MODERNISATION || "false";
      // },
    },
    [flags.FORCE_APP_MODE]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_FORCE_APP_MODE || "false",
    },
    [flags.ENHANCED_SEARCH]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_ENHANCED_SEARCH || "false",
      // },
    },
    [flags.B2C_DISABLE_AUTH]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_B2C_DISABLE_AUTH || "false",
    },
    [flags.ENHANCED_COUNTER]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_ENHANCED_COUNTER || "false",
    },
    [flags.ENHANCED_BROWSER_HISTORY_BUTTON]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant:
        import.meta.env.REACT_APP_FEATURE_FLAG_ENHANCED_BROWSER_HISTORY_BUTTON || "false",
    },
    [flags.WEB_MODERNISATION]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_WEB_MODERNISATION || "false",
    },
    [flags.CALCULATORS_LEGACY_INTEGRATION]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant:
        import.meta.env.REACT_APP_FEATURE_FLAG_CALCULATORS_LEGACY_INTEGRATION || "false",
    },
    [flags.AIDH_ENABLED]: {
      disabled: false,
      variants: {
        on: import.meta.env.REACT_APP_FEATURE_FLAG_AIDH_AVAILABLE_FROM
          ? {
              availableFrom: import.meta.env.REACT_APP_FEATURE_FLAG_AIDH_AVAILABLE_FROM,
            }
          : {},
        off: null,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_AIDH_ENABLED === "true" ? "on" : "off",
      contextEvaluator: (_context) =>
        import.meta.env.REACT_APP_FEATURE_FLAG_AIDH_ENABLED === "true" ? "on" : "off",
    },
    [flags.DRTC_ENABLED]: {
      disabled: false,
      variants: {
        on: import.meta.env.REACT_APP_FEATURE_FLAG_DRTC_AVAILABLE_FROM
          ? {
              availableFrom: import.meta.env.REACT_APP_FEATURE_FLAG_DRTC_AVAILABLE_FROM,
            }
          : {},
        off: null,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_DRTC_ENABLED === "true" ? "on" : "off",
      contextEvaluator: (_context) =>
        import.meta.env.REACT_APP_FEATURE_FLAG_DRTC_ENABLED === "true" ? "on" : "off",
    },
    [flags.OBESITY_ENABLED]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.REACT_APP_FEATURE_FLAG_OBESITY_ENABLED || "false",
    },
    [flags.PORTAL_ENABLED]: {
      disabled: false,
      variants: {
        on: {
          portalUrl: import.meta.env.VITE_PORTAL_URL || "https://portal.tg.org.au",
        },
        off: null,
      },
      defaultVariant: "on",
      contextEvaluator: (_context) =>
        import.meta.env.VITE_FEATURE_FLAG_PORTAL_ENABLED === "false" ? "off" : "on",
    },
    [flags.UPDATED_EXPERT_GROUPS]: {
      disabled: false,
      variants: {
        true: true,
        false: false,
      },
      defaultVariant: import.meta.env.UPDATED_EXPERT_GROUPS || "false",
    },
    [flags.ADPHA_B2C_PROMOTION]: {
      disabled: false,
      variants: {
        on: {
          products: ["2151"],
          startDate: "2026-03-09",
          endDate: "2026-03-31",
          discountType: "fixed",
          price: 159,
          originalPrice: 199,
          label: "Early bird",
          title: "Early Bird Offer",
          footnote: "*Early bird price ends 31 March",
        },
        off: null,
      },
      defaultVariant: "off",
      contextEvaluator: (_context) =>
        import.meta.env.VITE_FEATURE_FLAG_ADPHA_B2C_PROMOTION === "true" ? "on" : "off",
    },
  };

  return config;
}

async function initOpenFeatureProvider() {
  const flagConfig = createFlagConfig();
  try {
    const provider = getOpenFeatureProvider();
    const initPromise = OpenFeature.setProviderAndWait(provider);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Flagsmith timeout")), 5000),
    );

    await Promise.race([initPromise, timeoutPromise]);

    console.log("Flagsmith provider initialized successfully, using with InMemory fallback");
  } catch (error) {
    console.warn(
      "Flagsmith provider failed or timed out, falling back to InMemoryProvider:",
      error.message,
    );
    const fallbackMultiProvider = new WebMultiProvider(
      [{ provider: new InMemoryProvider(flagConfig) }],
      new FirstSuccessfulStrategy(),
    );
    await OpenFeature.setProviderAndWait(fallbackMultiProvider);
  }
}

function getOpenFeatureProvider() {
  const flagConfig = createFlagConfig();
  const isFlagsmith = import.meta.env.REACT_APP_FLAG_PROVIDER === "flagsmith";
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;

  if (isFlagsmith && !offline) {
    const flagsmithProvider = new FlagsmithClientProvider({
      environmentID: import.meta.env.REACT_APP_FLAGSMITH_ENV_KEY,
      cacheFlags: true,
      cacheOptions: {
        skipAPI: true, // Cache all flags without sending any context to the API
        cacheKey: "flagsmith_flags",
        ttl: 10 * 60 * 1000, // Cache for 10 minutes
      },
    });

    return new WebMultiProvider(
      [{ provider: flagsmithProvider }, { provider: new InMemoryProvider(flagConfig) }],
      new FirstSuccessfulStrategy(),
    );
  }

  console.log("Using InMemoryProvider (offline or flagsmith not configured)");
  return new WebMultiProvider(
    [{ provider: new InMemoryProvider(flagConfig) }],
    new FirstSuccessfulStrategy(),
  );
}

export { initOpenFeatureProvider };
